import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Stack, useMediaQuery } from '@mui/material';
import { DatePickerField, InputField, UploadAvatar } from 'components';
import ModalUI from 'components/common/ModalUI';
import { NotifyService } from 'config';
import yup from 'config/yup.custom';
import { SYS_MESS } from 'constants/systemMessage';
import { useDebounce } from 'hooks';
import { USER_TYPE } from 'models';
import { CHILDREN_LIST_RESPONSE } from 'models/parent/children';
import moment from 'moment';
import { FormEvent, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { uploadAPIs } from 'services/upload';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { setGlobalLoading } from 'store/reducers/global';
import {
  addChildrenAction,
  getChildrenListAction,
  updateChildrenAction,
} from 'store/reducers/parent/actionTypes';
import { theme } from 'theme';

interface IAddEditChildrenForm {
  full_name: string;
  childrenPicture: string;
  date_of_birth: string;
}

interface PopupAddEditChildrenProps {
  openPop: boolean;
  onClose: () => void;
  isEdit: boolean;
  selectedChildren?: CHILDREN_LIST_RESPONSE;
}

const schema = yup
  .object({
    full_name: yup.string().required(SYS_MESS.ERROR.NAME_OF_CHILD_REQUIRED),
    date_of_birth: yup.mixed().required(SYS_MESS.ERROR.AGE_REQUIRED),
    childrenPicture: yup.mixed().optional(),
  })
  .required();

export const PopupAddEditChildren = ({
  openPop,
  onClose,
  isEdit,
  selectedChildren,
}: PopupAddEditChildrenProps) => {
  const dispatch = useAppDispatch();

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { globalLoading } = useAppSelector((state) => state.global);

  const { handleSubmit, control, reset, setValue, watch } =
    useForm<IAddEditChildrenForm>({
      mode: 'onChange',
      resolver: yupResolver(schema),
    });

  const handleClose = () => {
    reset({
      full_name: '',
      childrenPicture: null,
      date_of_birth: null,
    });
    onClose();
  };

  const onSubmit = useDebounce(async (data: IAddEditChildrenForm) => {
    if (globalLoading) return;
    const dispatchAction = isEdit ? updateChildrenAction : addChildrenAction;
    const _params = {
      full_name: data.full_name,
      date_of_birth: data.date_of_birth,
      picture: data.childrenPicture,
      id: isEdit ? selectedChildren.id : undefined,
    };

    dispatch(
      dispatchAction({
        data: { ..._params },
        onSuccess: () => {
          handleClose();
          dispatch(getChildrenListAction());
        },
        onFailed: (e) => {
          NotifyService.error(e);
        },
      }),
    );
  }, 500);

  const onChangePicture = async (file: File) => {
    dispatch(setGlobalLoading(true));
    const formData = new FormData();
    formData.append('file', file);

    await uploadAPIs
      .uploadFile(formData)
      .then(({ data }) => {
        setValue('childrenPicture', data?.data.url);
      })
      .catch((e) => {
        NotifyService.error(e);
      })
      .finally(() => {
        dispatch(setGlobalLoading(false));
      });
  };

  useEffect(() => {
    if (isEdit) {
      reset({
        full_name: selectedChildren.full_name,
        date_of_birth: selectedChildren.date_of_birth,
        childrenPicture: selectedChildren.picture,
      });
    }
  }, [selectedChildren, isEdit, reset]);

  return (
    <ModalUI
      open={openPop}
      onClose={handleClose}
      title={isEdit ? 'Edit Children' : 'Add Children'}
      PaperProps={{ sx: { maxWidth: '600px', width: '100%' } }}
    >
      <Stack
        component="form"
        spacing={4}
        onSubmit={(event: FormEvent<HTMLFormElement>) => {
          event.stopPropagation();
          handleSubmit(onSubmit)(event);
        }}
      >
        <Stack
          direction={isMobile ? 'column' : 'row'}
          justifyContent={'space-between'}
          alignItems={'center'}
          spacing={4}
        >
          <Stack alignItems={'center'} width={isMobile ? '100%' : '40%'}>
            <UploadAvatar
              role={USER_TYPE.CHILDREN}
              onChange={(val) => onChangePicture(val)}
              value={watch('childrenPicture')}
              title="Upload Picture"
            />
          </Stack>
          <Stack spacing={2} width={isMobile ? '100%' : '60%'}>
            <InputField
              control={control}
              name={'full_name'}
              placeholder="What is your child’s name?"
              label="Name of Child"
            />
            <DatePickerField
              name={'date_of_birth'}
              control={control}
              iconColor="primary"
              placeholder="Date of birthday"
              label="Date of birthday"
              maxDate={moment()}
            />
          </Stack>
        </Stack>

        <Button variant="contained" type="submit" sx={{ width: '100%' }}>
          Save
        </Button>
      </Stack>
    </ModalUI>
  );
};

export default PopupAddEditChildren;
